import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Loader2, Send, Bot, User, Wrench, ShieldAlert } from "lucide-react";
import { agentChat, agentUsage } from "../lib/agentApi";
import { getIsAdmin } from "../lib/bannerApi";

const SUGGESTIONS = [
  "Run a full health check and list any problems",
  "How many events are in the database, by status?",
  "Find 10 real UK car meets for 2026 and add the ones that aren't duplicates",
  "Fix any events with missing coordinates",
];

export default function AdminAgent() {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [allowed, setAllowed] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null); // { progress, activity, status }
  const [usage, setUsage] = useState(null);
  const sessionId = useRef(
    window.crypto?.randomUUID?.() || `sess-${Date.now()}-${Math.random().toString(36).slice(2)}`
  );
  const scrollRef = useRef(null);

  useEffect(() => {
    getIsAdmin()
      .then((ok) => {
        setAllowed(!!ok);
        setChecking(false);
        if (ok) agentUsage().then(setUsage).catch(() => {});
      })
      .catch(() => {
        setAllowed(false);
        setChecking(false);
      });
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setBusy(true);
    setProgress({ progress: 0, activity: "Queued", status: "queued" });
    try {
      const res = await agentChat(sessionId.current, msg, (p) => setProgress(p));
      setMessages((m) => [...m, { role: "assistant", text: res.reply, steps: res.steps || [] }]);
      if (res.usage) setUsage(res.usage);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Request failed.";
      setMessages((m) => [...m, { role: "assistant", text: `⚠️ ${detail}`, error: true }]);
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  if (checking) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-[#050505] text-white">
        <Loader2 className="h-6 w-6 animate-spin text-[#ff2a00]" />
      </div>
    );
  }

  if (!allowed) {
    return (
      <div
        data-testid="agent-denied"
        className="flex min-h-[100dvh] flex-col items-center justify-center bg-[#050505] px-6 text-center text-white"
      >
        <ShieldAlert className="h-8 w-8 text-[#ff2a00]" />
        <p className="mt-3 font-display text-lg font-bold">Admins only</p>
        <button
          onClick={() => navigate("/")}
          className="mt-4 rounded-full bg-[#ff2a00] px-5 py-2.5 text-sm font-bold"
        >
          Back to RoadRnr
        </button>
      </div>
    );
  }

  return (
    <div data-testid="admin-agent-page" className="flex h-[100dvh] flex-col bg-[#050505] text-white">
      {/* Header */}
      <div className="shrink-0 border-b border-white/10 bg-[#050505]/90 px-5 pb-3 pt-6">
        <button
          data-testid="agent-back"
          onClick={() => navigate("/admin")}
          className="mb-2 inline-flex items-center gap-1 text-sm font-semibold text-white/60"
        >
          <ChevronLeft className="h-4 w-4" /> Admin
        </button>
        <div className="flex items-center justify-between gap-3">
          <h1 className="font-display flex items-center gap-2 text-2xl font-extrabold tracking-tight">
            <Bot className="h-6 w-6 text-[#ff2a00]" /> AI Admin Agent
          </h1>
          {usage && (
            <div data-testid="agent-usage" className="text-right text-[11px] leading-tight text-white/50">
              <div>
                Search: <span className="font-semibold text-white/80">{usage.searches_used}</span>/{usage.search_cap}
              </div>
              <div>~${usage.estimated_cost_usd} · {usage.gemini_calls} calls</div>
            </div>
          )}
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-5">
        {messages.length === 0 && (
          <div className="mx-auto max-w-md pt-6">
            <p className="text-center text-sm text-white/50">
              Talk to RoadRnr's private admin agent. It can research and add real events, inspect and repair the
              database, and answer questions. Try:
            </p>
            <div className="mt-4 grid gap-2">
              {SUGGESTIONS.map((s, i) => (
                <button
                  key={i}
                  data-testid={`agent-suggestion-${i}`}
                  onClick={() => send(s)}
                  className="rounded-2xl border border-white/10 bg-[#0e0e0e] px-4 py-3 text-left text-sm text-white/80 transition-colors hover:border-[#ff2a00]/40"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <div key={i} className={`flex gap-2.5 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            {m.role === "assistant" && (
              <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#ff2a00]/15">
                <Bot className="h-4 w-4 text-[#ff2a00]" />
              </div>
            )}
            <div
              data-testid={`agent-msg-${m.role}`}
              className={`max-w-[82%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                m.role === "user"
                  ? "bg-[#ff2a00] text-white"
                  : m.error
                  ? "border border-[#ff2a00]/40 bg-[#ff2a00]/10 text-white"
                  : "border border-white/10 bg-[#101010] text-white/90"
              }`}
            >
              {m.text}
              {m.steps && m.steps.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5 border-t border-white/10 pt-2">
                  {m.steps.map((s, j) => (
                    <span
                      key={j}
                      className="inline-flex items-center gap-1 rounded-full bg-white/5 px-2 py-0.5 text-[10px] font-medium text-white/50"
                    >
                      <Wrench className="h-2.5 w-2.5" /> {s.tool}
                    </span>
                  ))}
                </div>
              )}
            </div>
            {m.role === "user" && (
              <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/10">
                <User className="h-4 w-4 text-white/70" />
              </div>
            )}
          </div>
        ))}

        {busy && (
          <div data-testid="agent-thinking" className="flex items-start gap-2.5">
            <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#ff2a00]/15">
              <Bot className="h-4 w-4 text-[#ff2a00]" />
            </div>
            <div className="min-w-0 flex-1 max-w-[82%] rounded-2xl border border-white/10 bg-[#101010] px-4 py-3">
              <div className="flex items-center justify-between gap-2">
                <span data-testid="agent-progress-activity" className="flex items-center gap-2 text-sm text-white/80">
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-[#ff2a00]" />
                  {progress?.activity || "Working"}
                </span>
                <span data-testid="agent-progress-percent" className="font-display text-sm font-bold tabular-nums text-[#ff2a00]">
                  {Math.round(progress?.progress ?? 0)}%
                </span>
              </div>
              <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-white/10">
                <div
                  data-testid="agent-progress-bar"
                  className="h-full rounded-full bg-[#ff2a00] transition-all duration-500 ease-out"
                  style={{ width: `${Math.max(3, Math.min(progress?.progress ?? 0, 100))}%` }}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Composer */}
      <div className="shrink-0 border-t border-white/10 bg-[#080808] px-4 pb-6 pt-3">
        <div className="mx-auto flex max-w-md items-end gap-2">
          <textarea
            data-testid="agent-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            rows={1}
            placeholder="Ask the agent… (e.g. 'fix it', 'add the next 20')"
            className="max-h-32 min-h-[46px] flex-1 resize-none rounded-2xl border border-white/10 bg-[#121212] px-4 py-3 text-base text-white placeholder:text-white/40 focus:border-[#ff2a00]/50 focus:outline-none"
          />
          <button
            data-testid="agent-send"
            onClick={() => send()}
            disabled={busy || !input.trim()}
            className="flex h-[46px] w-[46px] shrink-0 items-center justify-center rounded-2xl bg-[#ff2a00] text-white transition-transform active:scale-95 disabled:opacity-40"
          >
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
          </button>
        </div>
      </div>
    </div>
  );
}
